"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, Globe as Globe2, Layers, ChartLine as LineChart, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const pillars = [{
  title: "Technical SEO foundations",
  description: "Clean site architecture, indexation control, and web performance built for complex web apps.",
  icon: Layers
}, {
  title: "Content-led organic growth",
  description: "High-intent search strategies designed to attract qualified trials, demos, and enterprise accounts.",
  icon: LineChart
}, {
  title: "International market reach",
  description: "Hreflang precision and multi-region localization for SaaS platforms scaling across borders.",
  icon: Globe2
}];
const directBenefits = ["Direct consultant access on every call", "Tailored strategy, no recycled agency playbooks", "Transparent reporting and verified deliverables"];
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hero" data-nav="light" className="relative overflow-hidden bg-background pt-12 pb-20 md:pt-20 md:pb-28 border-b border-border">
      {/* Subtle brand blush decorative accents */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 right-0 size-96 rounded-full bg-secondary/60 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 left-1/4 size-80 rounded-full bg-secondary/40 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Main Copy Column */}
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="lg:col-span-7 flex flex-col items-start">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/70 px-3.5 py-1 text-xs font-medium text-secondary-foreground mb-6">
              <Sparkles className="size-3.5 text-primary" />
              <span>Independent SEO Consultant for SaaS & International B2B</span>
            </div>

            <h1 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground text-balance leading-tight">
              Professional SEO consultant for sustainable search growth
            </h1>

            <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl font-sans">
              I partner with software leaders and growing enterprises to turn organic search into a predictable customer acquisition channel. From foundational technical health to high-converting content engines, you work directly with me — no account managers, no junior staff handoffs.
            </p>

            {/* CTAs */}
            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-6 py-3 text-sm font-medium shadow-sm transition-[color,background-color,border-color,box-shadow]">
                <Link to="/ContactPage">
                  Book a free SEO consultation
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border bg-background text-foreground hover:bg-muted/50 rounded-md px-6 py-3 text-sm font-medium transition-[color,background-color,border-color,box-shadow]">
                <Link to="/CaseStudiesPage">
                  Explore my case studies
                </Link>
              </Button>
            </div>

            {/* Direct Consultant Value Props */}
            <div className="mt-10 pt-8 border-t border-border w-full">
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3 font-default">
                Why SaaS founders and marketing leaders work with me
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {directBenefits.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-2 text-xs sm:text-sm text-foreground">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>)}
              </ul>
            </div>
          </motion.div>

          {/* Consultant Visual Card Column */}
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              {/* Outer decorative border frame */}
              <div className="relative rounded-2xl border border-border bg-card p-3 sm:p-4 shadow-xl">
                <div className="relative overflow-hidden rounded-xl bg-muted aspect-[4/5] sm:aspect-[3/4]">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-sizes" alt="Yassine Essaadi, independent freelance professional SEO consultant" loading="eager" decoding="async" className="h-full w-full object-cover object-top" />
                  
                  {/* Floating Identity Badge inside image frame */}
                  <div className="absolute inset-x-3 bottom-3 rounded-lg border border-border/80 bg-background/95 p-3.5 backdrop-blur-md shadow-md">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-default font-semibold text-sm text-foreground">
                          Yassine Essaadi
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Principal Freelance SEO Consultant
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-[11px] font-medium">
                        UK & Global
                      </Badge>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-xs font-medium text-primary">
                      <span className="inline-block size-2 rounded-full bg-chart-4 animate-pulse" />
                      <span>Available for select consultations</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Verified Results micro-card */}
              <div className="hidden sm:flex absolute -bottom-6 -left-6 items-center gap-3 rounded-xl border border-border bg-background p-3.5 shadow-lg max-w-xs">
                <div className="rounded-lg bg-secondary p-2.5 text-primary shrink-0">
                  <LineChart className="size-5" />
                </div>
                <div>
                  <div className="font-default font-bold text-sm text-foreground">
                    SaaS organic pipeline
                  </div>
                  <div className="text-xs text-muted-foreground leading-snug">
                    Search strategies focused on trials and demo requests.
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* 3 Core SaaS Pillars Grid */}
        <div className="mt-16 sm:mt-24 pt-12 border-t border-border">
          <div className="mb-6">
            <h2 className="font-default text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
              A specialized framework for ambitious software companies
            </h2>
            <p className="mt-1 text-sm sm:text-base text-muted-foreground">
              Everything I implement is engineered around product adoption, retention, and qualified demand.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {pillars.map((pillar, i) => {
            const Icon = pillar.icon;
            return <div key={pillar.title} data-index={i} className="rounded-xl border border-border bg-card p-6 shadow-xs hover:border-primary/40 transition-[color,background-color,border-color,box-shadow]">
                  <div className="inline-flex items-center justify-center size-10 rounded-lg bg-secondary text-primary mb-4">
                    <Icon className="size-5" />
                  </div>
                  <h3 className="font-default font-semibold text-base text-card-foreground">
                    {pillar.title}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {pillar.description}
                  </p>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}